import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../cn';
import type { ColorName, ColorVariant, Style } from './buttonClasses';
import { composeVariant } from './buttonClasses';
import type { Size } from './types';

/** A single stop in a multi-state {@link ToggleSwitch}. */
export type ToggleOption<T> = {
  value: T;
  /** Icon rendered inside the sliding thumb when this option is active. */
  thumbIcon?: ReactNode;
  /** Track color when this option is active. Omit for a neutral gray track. */
  color?: ColorName;
  /** Accessible label for this state (used as the button's `aria-label`). */
  label?: string;
};

type SharedToggleProps = {
  size?: Size;
  /** Color axis — `'blue' | 'red' | 'green' | …`. Defaults to `'gray'`. */
  color?: ColorName;
  /** Style axis — `'filled' | 'outline' | 'soft'`. Defaults to `'filled'`. */
  tone?: Style;
};

export type BinaryToggleSwitchProps = SharedToggleProps &
  Omit<ComponentProps<'button'>, 'size' | 'onChange' | 'color'> & {
    checked?: boolean;
    onChange?: (checked: boolean) => void;
  };

export type MultiToggleSwitchProps<T> = SharedToggleProps &
  Omit<ComponentProps<'button'>, 'size' | 'onChange' | 'color' | 'value'> & {
    /** Ordered stops (2 or more). The thumb sits at the active value's index. */
    options: ToggleOption<T>[];
    value: T;
    /** Static icon painted at the left end of the track, behind the thumb. */
    leftIcon?: ReactNode;
    /** Static icon painted at the right end of the track, behind the thumb. */
    rightIcon?: ReactNode;
  };

export type ToggleSwitchProps<T = boolean> = BinaryToggleSwitchProps | MultiToggleSwitchProps<T>;

function isMulti<T>(props: ToggleSwitchProps<T>): props is MultiToggleSwitchProps<T> {
  return 'options' in props && Array.isArray(props.options);
}

// Track dimensions based on size
function trackSize(size: Size = 'md') {
  return {
    '3xs': 'h-3 w-5',
    '2xs': 'h-3.5 w-6',
    xs: 'h-4 w-7',
    sm: 'h-5 w-9',
    md: 'h-6 w-11',
    lg: 'h-7 w-12',
    xl: 'h-8 w-14',
    '2xl': 'h-10 w-18',
    '3xl': 'h-12 w-22',
    '4xl': 'h-14 w-26',
  }[size];
}

// Thumb size (slightly smaller than track height)
function thumbSize(size: Size = 'md') {
  return {
    '3xs': 'size-2',
    '2xs': 'size-2.5',
    xs: 'size-3',
    sm: 'size-4',
    md: 'size-5',
    lg: 'size-6',
    xl: 'size-7',
    '2xl': 'size-9',
    '3xl': 'size-11',
    '4xl': 'size-13',
  }[size];
}

// Thumb translate when checked: track width − thumb size − 2px trailing gap.
// The 2px trailing gap matches the 2px vertical gap (thumb is 4px shorter than
// the track) and the 2px leading gap (`translate-x-0.5`) when off, so the thumb
// sits equidistant from the top, bottom, and trailing edge.
function thumbTranslate(size: Size = 'md') {
  return {
    '3xs': 'translate-x-2.5',
    '2xs': 'translate-x-3',
    xs: 'translate-x-3.5',
    sm: 'translate-x-4.5',
    md: 'translate-x-5.5',
    lg: 'translate-x-5.5',
    xl: 'translate-x-6.5',
    '2xl': 'translate-x-8.5',
    '3xl': 'translate-x-10.5',
    '4xl': 'translate-x-12.5',
  }[size];
}

// Pixel mirrors of trackSize()/thumbSize() — used to place the multi-state thumb
// and size the static end icons, which can't be expressed as static Tailwind
// classes for an arbitrary number of stops. Keep in sync with the tables above.
const TRACK_WIDTH_PX: Record<Size, number> = {
  '3xs': 20,
  '2xs': 24,
  xs: 28,
  sm: 36,
  md: 44,
  lg: 48,
  xl: 56,
  '2xl': 72,
  '3xl': 88,
  '4xl': 104,
};
const THUMB_WIDTH_PX: Record<Size, number> = {
  '3xs': 8,
  '2xs': 10,
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 28,
  '2xl': 36,
  '3xl': 44,
  '4xl': 52,
};
// Equidistant gap from every edge (thumb is 4px shorter than the track height).
const TRACK_PADDING_PX = 2;

// Variant colors for the track when checked / active
function variantCheckedBg(variant: ColorVariant = 'gray') {
  const v = variant.endsWith('Outline') ? variant.replace('Outline', '') : variant;
  switch (v) {
    case 'red':
      return 'bg-fair-pink-500 dark:bg-fair-pink-600';
    case 'green':
      return 'bg-mantis-500 dark:bg-mantis-600';
    case 'yellow':
      return 'bg-qsw-500 dark:bg-qsw-600';
    case 'purple':
      return 'bg-purple-500 dark:bg-purple-600';
    case 'aurora':
      return 'bg-linear-to-r from-[#4D4ACC] to-[#792D86]';
    case 'blue':
      return 'bg-astronaut-500 dark:bg-astronaut-600';
    default:
      return 'bg-astronaut-500 dark:bg-astronaut-600';
  }
}

export default function ToggleSwitch<T = boolean>(props: ToggleSwitchProps<T>) {
  if (isMulti(props)) {
    return <MultiToggleSwitch {...props} />;
  }
  return <BinaryToggleSwitch {...props} />;
}

function BinaryToggleSwitch({
  size = 'md',
  color,
  tone,
  checked = false,
  disabled,
  onChange,
  className,
  ...props
}: BinaryToggleSwitchProps) {
  const effectiveVariant: ColorVariant = composeVariant(color ?? 'gray', tone ?? 'filled');
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={cn(
        'relative inline-flex shrink-0 items-center rounded-full transition-colors',
        trackSize(size),
        checked ? variantCheckedBg(effectiveVariant) : 'bg-gray-300 dark:bg-gray-600',
        disabled && 'opacity-50 cursor-not-allowed',
        !disabled && 'cursor-pointer',
        className,
      )}
      {...props}>
      <span
        className={cn(
          'inline-block rounded-full bg-white shadow-xs transition-transform',
          thumbSize(size),
          checked ? thumbTranslate(size) : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

function MultiToggleSwitch<T>({
  options,
  value,
  leftIcon,
  rightIcon,
  size = 'md',
  color,
  tone,
  disabled,
  className,
  ...props
}: MultiToggleSwitchProps<T>) {
  const activeIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );
  const active = options[activeIndex];
  const stops = options.length;

  // Same width as a regular toggle — the thumb just stops at N evenly-spaced
  // positions across the track (left / middle / right for 3 states). `travel` is
  // the span the thumb sweeps; the static end icons are sized to it so they tuck
  // into the zone the far thumb never reaches and are never clipped by it.
  const thumbPx = THUMB_WIDTH_PX[size];
  const travelPx = TRACK_WIDTH_PX[size] - 2 * TRACK_PADDING_PX - thumbPx;
  const offset = TRACK_PADDING_PX + (stops > 1 ? (activeIndex / (stops - 1)) * travelPx : 0);
  const iconBoxPx = Math.max(0, travelPx);

  const resolvedColor = active?.color ?? color;
  // Medium neutral (not the lighter gray-300 used by the binary OFF track) so the
  // white left/right icons stay legible when an option has no color of its own.
  const trackBg = resolvedColor
    ? variantCheckedBg(composeVariant(resolvedColor, tone ?? 'filled'))
    : 'bg-gray-400 dark:bg-gray-600';

  return (
    <button
      type="button"
      aria-label={active?.label}
      disabled={disabled}
      className={cn(
        'relative inline-flex shrink-0 items-center rounded-full transition-colors',
        trackSize(size),
        trackBg,
        disabled && 'opacity-50 cursor-not-allowed',
        !disabled && 'cursor-pointer',
        className,
      )}
      {...props}>
      {leftIcon && (
        <span
          className="absolute inset-y-0 flex items-center justify-center text-white"
          style={{ left: TRACK_PADDING_PX, width: iconBoxPx }}>
          {leftIcon}
        </span>
      )}
      {rightIcon && (
        <span
          className="absolute inset-y-0 flex items-center justify-center text-white"
          style={{ right: TRACK_PADDING_PX, width: iconBoxPx }}>
          {rightIcon}
        </span>
      )}
      <span
        className={cn(
          'absolute left-0 inline-flex items-center justify-center rounded-full bg-white text-gray-700 shadow-xs transition-transform',
          thumbSize(size),
        )}
        style={{ transform: `translateX(${offset}px)`, top: TRACK_PADDING_PX }}>
        {active?.thumbIcon}
      </span>
    </button>
  );
}
